import { Button, SkeletonLoader, Tooltip } from '@toggle/design-system';
import { DOW_JONES, SingleNews } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorMessageWrapper } from '~/components/error-message-wrapper/ErrorMessageWrapper';
import { config } from '~/config/app.config';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';

import styles from './ArticleDrawer.module.scss';
import {
  contentSkeletonAreas,
  SingleArticle,
} from './components/single-article/SingleArticle';
import singleArticleStyles from './components/single-article/SingleArticle.module.scss';

interface ArticleDrawerContentProps {
  isError?: boolean;
  isFetching?: boolean;
  data?: SingleNews;
  onArticleClick?: (singleNews: SingleNews) => void;
  onClose?: () => void;
  list?: SingleNews[];
  newsId: string;
  disableFooter?: boolean;
}

export const ArticleDrawerContent = ({
  isError = false,
  isFetching = false,
  data,
  onArticleClick,
  onClose,
  list = [],
  newsId,
  disableFooter = false,
}: ArticleDrawerContentProps) => {
  const { t } = useTranslation(['news', 'chart', 'analyze', 'countries']);
  const { navigateToPage } = useNavigateToAssetOverview();

  const newsIndex = list.findIndex(item => item.id === newsId);
  const nextArticle = list[newsIndex + 1];
  const prevArticle = list[newsIndex - 1];
  const hasFooter = !!nextArticle || !!prevArticle;

  const navigateToAssetOverview = (entityTag: string) => {
    onClose?.();
    navigateToPage({ entityTag });
  };

  const renderContent = () => {
    if (isFetching) {
      return (
        <div className={singleArticleStyles.contentSkeletonWrapper}>
          <SkeletonLoader
            data-testid="loading-state"
            areas={contentSkeletonAreas}
          />
        </div>
      );
    }

    if (isError && !isFetching) {
      return (
        <div className={styles.errorMessage}>
          <ErrorMessageWrapper
            data-testid="error-state"
            displayIcon
            variant="error"
          />
        </div>
      );
    }

    if (!!data) {
      return (
        <SingleArticle
          article={data}
          isLoading={isFetching}
          onEntityClick={
            config.app.isTeamsApp ? () => void 0 : navigateToAssetOverview
          }
        />
      );
    }

    return null;
  };

  const renderDisclaimer = () => {
    if (data?.source === DOW_JONES) {
      return (
        <span className={styles.dowJonesDisclaimer}>
          {t('news:dowJonesDisclaimer.dowJonesCopyright', {
            year: new Date().getFullYear(),
          })}
        </span>
      );
    }

    return null;
  };

  return (
    <>
      <div
        className={clsx(styles.content, {
          [styles.noFooter]: !hasFooter || disableFooter,
        })}
      >
        {renderContent()}
        {renderDisclaimer()}
      </div>
      {!disableFooter && hasFooter && (
        <footer className={styles.footer} data-testid="article-drawer-footer">
          <div>
            {!!prevArticle && onArticleClick && (
              <Tooltip label={prevArticle.title}>
                <Button
                  data-testid="prev-article"
                  label={t('news:drawer.prevArticle')}
                  variant="empty"
                  size="xsmall"
                  iconName="ArrowUp"
                  iconPosition="right"
                  onClick={() => onArticleClick(prevArticle)}
                />
              </Tooltip>
            )}
          </div>

          <div>
            {!!nextArticle && onArticleClick && (
              <Tooltip label={nextArticle.title}>
                <Button
                  data-testid="next-article"
                  label={t('news:drawer.nextArticle')}
                  variant="empty"
                  size="xsmall"
                  iconName="ArrowDown"
                  iconPosition="right"
                  onClick={() => onArticleClick(nextArticle)}
                />
              </Tooltip>
            )}
          </div>
        </footer>
      )}
    </>
  );
};
